import React, { lazy } from "react";
import { defer, Link } from "react-router-dom";
import RouteLoader from "../UI/RouteLoader";
import { getStudentRequest } from "../../requests/students";
import { getEnrollmentRequest } from "../../requests/enrollments";
import { withSuspense } from "./routeHelpers";

const Students = lazy(
  () => import(/* webpackChunkName: "students" */ "../Students/Students")
);
const StudentOverview = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentOverview/StudentOverview"
    )
);
const Student = lazy(
  () => import(/* webpackChunkName: "students" */ "../Students/Student/Student")
);
const StudentProfile = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentProfile/StudentProfile"
    )
);
const StudentCourses = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentCourses"
    )
);
const StudentSeminars = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentSeminars"
    )
);
const StudentDecisions = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentDecisions/StudentDecisions"
    )
);
const StudentProducts = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentProducts/StudentProducts"
    )
);
const StudentNotes = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentNotes/StudentNotes"
    )
);
const StudentNew = lazy(
  () =>
    import(/* webpackChunkName: "students" */ "../Students/Student/StudentNew")
);
const StudentCourse = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/StudentCourse"
    )
);
const Management = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Management/Management"
    )
);
const Lessons = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Lessons/Lessons"
    )
);
const Decisions = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Decisions/Decisions"
    )
);
const Questions = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Questions/Questions"
    )
);
const Products = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Products/Products"
    )
);
const Reminders = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Reminders/Reminders"
    )
);
const Visits = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Visits/Visits"
    )
);
const StudentSeminar = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/StudentSeminar"
    )
);
const Presentations = lazy(
  () =>
    import(
      /* webpackChunkName: "students" */ "../Students/Student/StudentCoursesAndSeminars/StudentEnrollment/Presentations/Presentations"
    )
);

export default [
  {
    path: "/students",
    element: withSuspense(Students),
  },
  {
    path: "/students/new",
    element: withSuspense(StudentNew),
  },
  {
    path: "/students/:studentId",
    element: (
      <RouteLoader>
        {(loader) => withSuspense(<Student student={loader?.data} />)}
      </RouteLoader>
    ),
    id: "student",
    loader: ({ params }) => {
      return defer({
        data: getStudentRequest({ studentId: params.studentId }),
      });
    },
    handle: {
      actionMenu: "students",
      crumb: ({ data }) => (
        <span>
          <Link to="/students">STUDENTS</Link> /{" "}
          <Link to={`/students/${data?.id}/overview`}>
            {data?.last_name?.toUpperCase()}
          </Link>
        </span>
      ),
    },
    children: [
      {
        path: "/students/:studentId/overview",
        element: withSuspense(StudentOverview),
        handle: {
          actionMenu: "students",
        },
      },
      {
        path: "/students/:studentId/profile",
        element: withSuspense(StudentProfile),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ PROFILE</span>,
        },
      },
      {
        path: "/students/:studentId/courses",
        element: withSuspense(StudentCourses),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ COURSES</span>,
        },
      },
      {
        path: "/students/:studentId/courses/enrollments/:enrollmentId",
        element: (
          <RouteLoader>
            {(loader) =>
              withSuspense(<StudentCourse enrollment={loader?.data} />)
            }
          </RouteLoader>
        ),
        id: "courseEnrollment",
        loader: ({ params }) => {
          return defer({
            data: getEnrollmentRequest({ enrollmentId: params.enrollmentId }),
          });
        },
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ COURSE NAME</span>,
        },
        children: [
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/management",
            element: withSuspense(Management),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/lessons",
            element: withSuspense(Lessons),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/decisions",
            element: withSuspense(Decisions),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/questions",
            element: withSuspense(Questions),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/products",
            element: withSuspense(Products),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/reminders",
            element: withSuspense(Reminders),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/courses/enrollments/:enrollmentId/visits",
            element: withSuspense(Visits),
            handle: {
              actionMenu: "students",
            },
          },
        ],
      },
      {
        path: "/students/:studentId/seminars",
        element: withSuspense(StudentSeminars),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ SEMINARS</span>,
        },
      },
      {
        path: "/students/:studentId/seminars/enrollments/:enrollmentId",
        element: (
          <RouteLoader>
            {(loader) =>
              withSuspense(<StudentSeminar enrollment={loader?.data} />)
            }
          </RouteLoader>
        ),
        id: "seminarEnrollment",
        loader: ({ params }) => {
          return defer({
            data: getEnrollmentRequest({ enrollmentId: params.enrollmentId }),
          });
        },
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ SEMINARS</span>,
        },
        children: [
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/management",
            element: withSuspense(Management),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/presentations",
            element: withSuspense(Presentations),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/decisions",
            element: withSuspense(Decisions),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/visits",
            element: withSuspense(Visits),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/reminders",
            element: withSuspense(Reminders),
            handle: {
              actionMenu: "students",
            },
          },
          {
            path: "/students/:studentId/seminars/enrollments/:enrollmentId/products",
            element: withSuspense(Products),
            handle: {
              actionMenu: "students",
            },
          },
        ],
      },
      {
        path: "/students/:studentId/decisions",
        element: withSuspense(StudentDecisions),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ DECISIONS</span>,
        },
      },
      {
        path: "/students/:studentId/products",
        element: withSuspense(StudentProducts),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ PRODUCTS</span>,
        },
      },
      {
        path: "/students/:studentId/notes",
        element: withSuspense(StudentNotes),
        handle: {
          actionMenu: "students",
          crumb: () => <span className="pl-1">/ NOTES</span>,
        },
      },
    ],
  },
];
